import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, Clock, MapPin, ArrowLeft, User } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ViewAttendance = () => {
  const { attendanceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);

  useEffect(() => {
    fetchAttendance();
  }, [attendanceId]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      // Fetch single attendance record
      const response = await axiosInstance.get(`/attendance/${attendanceId}`);
      setAttendance(response.data.data);
    } catch (err) {
      Alert.error('Failed to load attendance details');
      console.error(err);
      navigate('/attendance');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading attendance details..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!attendance) {
    return null;
  }

  return (
    <DashboardLayout>
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <button
                    onClick={() => navigate('/attendance')}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-buttonBg" />
                      Attendance Details
                    </h1>
                    <p className="text-sm text-gray-600">View complete attendance record</p>
                  </div>
                </div>

                {/* Status Banner */}
                <div className={`p-4 rounded-lg mb-6 ${
                  attendance.is_logged_out 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {attendance.is_logged_out ? 'Completed Session' : 'Active Session'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {attendance.is_logged_out 
                          ? `Total work duration: ${attendance.work_hours_formatted}` 
                          : 'Session is currently ongoing'
                        }
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      attendance.is_logged_out ? 'bg-green-200' : 'bg-yellow-200'
                    }`}>
                      <Clock className={`w-6 h-6 ${
                        attendance.is_logged_out ? 'text-green-700' : 'text-yellow-700'
                      }`} />
                    </div>
                  </div>
                </div>

                {/* User Information */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-500" />
                    User Information
                  </h2>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">User Name</label>
                      <p className="text-base text-gray-900 font-medium">{attendance.user_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Date</label>
                      <p className="text-base text-gray-900 font-medium">{formatDate(attendance.attendance_date)}</p>
                    </div>
                  </div>
                </div>

                {/* Time Information */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-500" />
                    Time Information
                  </h2>
                  <div className="grid grid-cols-2 gap-2 sm:gap-6">
                    {/* Login Time */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-700 mb-2">Login Time</p>
                      <p className="text-2xl font-bold text-blue-900">{formatTime(attendance.login_time)}</p>
                    </div>

                    {/* Logout Time */}
                    <div className={`p-4 rounded-lg border ${
                      attendance.is_logged_out 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <p className={`text-sm font-medium mb-2 ${
                        attendance.is_logged_out ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        Logout Time
                      </p>
                      <p className={`text-2xl font-bold ${
                        attendance.is_logged_out ? 'text-green-900' : 'text-gray-900'
                      }`}>
                        {attendance.is_logged_out ? formatTime(attendance.logout_time) : 'Still Active'}
                      </p>
                    </div>

                    {/* Work Duration */}
                    <div className="sm:col-span-2 bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <p className="text-sm font-medium text-orange-700 mb-2">Total Work Duration</p>
                      <p className="text-3xl font-bold text-orange-900">{attendance.work_hours_formatted}</p>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    Location Information
                  </h2>

                  <div className="grid grid-cols-2 lg:grid-cols-2 gap-2 sm:gap-6">
                    {/* Login Location */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Login Location
                      </h3>
                      {attendance.login_location ? (
                        <div className="space-y-2 bg-blue-50 p-4 rounded-lg">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Village:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.login_location.village}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">City:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.login_location.city}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">District:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.login_location.district}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">State:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.login_location.state}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Country:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.login_location.country}</span>
                          </div>
                          <div className="pt-2 border-t border-blue-200">
                            <span className="text-xs text-gray-600">Coordinates:</span>
                            <p className="text-xs font-mono text-gray-900">
                              {attendance.login_location.latitude}, {attendance.login_location.longitude}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500">No location data available</p>
                      )}
                    </div>

                    {/* Logout Location */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Logout Location
                      </h3>
                      {attendance.logout_location ? (
                        <div className="space-y-2 bg-green-50 p-4 rounded-lg">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Village:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.logout_location.village}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">City:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.logout_location.city}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">District:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.logout_location.district}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">State:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.logout_location.state}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Country:</span>
                            <span className="text-sm font-medium text-gray-900">{attendance.logout_location.country}</span>
                          </div>
                          <div className="pt-2 border-t border-green-200">
                            <span className="text-xs text-gray-600">Coordinates:</span>
                            <p className="text-xs font-mono text-gray-900">
                              {attendance.logout_location.latitude}, {attendance.logout_location.longitude}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-gray-500">
                            {attendance.is_logged_out ? 'No location data available' : 'User has not logged out yet'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button variant="secondary" onClick={() => navigate('/attendance')}>
                    Back to List
                  </Button>
                </div>
              </div>
    </DashboardLayout>
  );
};

export default ViewAttendance;